import { useEffect, useState, useMemo } from 'react';
import './Header.scss'
import TLEContainer from './TLEContainer';
import RightContainer from './RightContainer';
import Notification from './Notification';
import useUrlParams from '../../hooks/useUrlParams';
import ScenariInfoContainer from './ScenariInfoContainer';
import ValidateSessionInfoContainer from './ValidateSessionInfoContainer';
import ValidateSubsystemDiagramButton from './ValidateSubsystemDiagramButton';
import useUrlNavigation from '../../hooks/useUrlNavigation';
import { SatelliteGroupType, SelectedSatelliteType } from '../../types';
import useSatellite from '../../hooks/useSatellite';
import MultigroupSelect from '../../custom_components/multigroup_select/MultigroupSelect';
import useLoader from '../../hooks/useLoader';
import { AcpLogo, BackSvg } from '../../../CommonComponents/svgs/Svgs';
import { RemoteAppSvg, ValidateSessionInfoSvg } from './Svgs';
import useScenario from '../../hooks/useScenario';
import useHwil from '../../hooks/useHwil';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';
import FaultInjectionHeader from './FaultInjectionHeader';
import { CircularProgress } from '@mui/material';
import { isOperateMode } from '../../hooks/utils';
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../simulate/constants';

const Header = () => {
  const { loading } = useLoader()
  const { scenario, deploymentSessionType } = useScenario()
  const { dashboard, sat_id } = useUrlParams();
  const { remoteAppLoader, setRemoteConnectionModal, remoteConnectionStatus } = useHwil()
  const { navigate_dashboard, update_command_center_url, navigate_home } = useUrlNavigation()
  const { satellites, selected_satellite, set_selected_satellite, satellite_groups, selected_satellite_group, set_selected_satellite_group } = useSatellite()

  const [showNotification, setShowNotifications] = useState(false);
  const [showTleInfo, setShowTleInfo] = useState(false)
  const [showScenarioInfo, setShowScenarioInfo] = useState(false)

  const remoteConnected = useMemo(() => remoteConnectionStatus.every(obj => obj.status === 'CONNECTED'), [remoteConnectionStatus])

  const filter_satellites_Groups = useMemo(() => satellite_groups ? satellite_groups.filter((sat_group) => sat_group.value.satelliteGroupId) : [], [satellite_groups])
  const filter_satellites = useMemo(() => satellites ? satellites.filter((sat) => sat.value.satelliteId) : [], [satellites])

  const select_satellite = (sat: SelectedSatelliteType) => {
    set_selected_satellite(sat)
    update_command_center_url(sat.value.satelliteGroupId, sat.value.satelliteId)
  }

  const select_group = (sat_group: SatelliteGroupType) => {
    if (!sat_id) return
    const selectedSatellite = sat_group.value.satellites?.[0]
    set_selected_satellite({
      label: selectedSatellite.satelliteName,
      value: {
        ...selectedSatellite,
        satelliteGroupId: sat_group.value.satelliteGroupId,
      }
    })
    set_selected_satellite_group(sat_group)
    update_command_center_url(sat_group.value.satelliteGroupId, selectedSatellite.satelliteId)
  }


  const operateMode = isOperateMode(dashboard, scenario)

  const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase();
  const isValidateSummary = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE && dashboard === 'summary'
  const isValidateRunningSession = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE
  const normalizedSessionType = String(deploymentSessionType ?? '').trim().toUpperCase()
  const isFlatsatValidateSession = normalizedSessionType === 'FLATSAT'
  const isTruetwinValidateSession = normalizedSessionType === 'TRUETWIN'
  const toggle_scenario__info = () => {
    if (deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE) {
      setShowScenarioInfo(!showScenarioInfo);
    } else if (operateMode) {
      setShowTleInfo(!showTleInfo);
    } else {
      setShowScenarioInfo(!showScenarioInfo);
    }
    setShowNotifications(false);
  }


  const show_notification = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowNotifications(true);
    setShowTleInfo(false);
    setShowScenarioInfo(false);
  }

  useEffect(() => {
    const onClick = () => {
      setShowTleInfo(false)
      setShowScenarioInfo(false)
      setShowNotifications(false)
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick)
  }, [])



  return (
    <div className='command__center_header'>
      <div className='header_left_container'>
        <button className='left__logo__button__container' onClick={navigate_home}>
          <AcpLogo />
        </button>
        <div className='left__button__container'>
          <button className='left__back__button' onClick={navigate_dashboard}>
            <BackSvg />
          </button>
          <MultigroupSelect
            groups={filter_satellites_Groups}
            selected_group={selected_satellite_group}
            satellites={filter_satellites}
            selected_satellite={selected_satellite}
            set_selected_group={(group) => { select_group(group) }}
            set_selected_satellite={(satellite) => { select_satellite(satellite) }}
            toggle_scenario__info={toggle_scenario__info}
            loading={loading && !isValidateSummary}
            infoTooltip={deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE ? 'Session info' : 'Orbit Details'}
            infoIcon={deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE ? <ValidateSessionInfoSvg /> : undefined}
          />
          {/* Validate: render popup inside this container so it positions relative to the globe icon */}
          {deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE && (
            <ValidateSessionInfoContainer open={showScenarioInfo} close={toggle_scenario__info} />
          )}
        </div>

        {isValidateRunningSession && (isFlatsatValidateSession || isTruetwinValidateSession) && (
          <ValidateSubsystemDiagramButton autoOpenOnMount={isValidateRunningSession} />
        )}

        {scenario?.status === 'SimulationRunning' && (scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP || scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.QUICK_DEPLOY) &&
          <CustomToolTip title='Connection Status' placement='bottom'>
            <button
              disabled={remoteAppLoader || !remoteConnectionStatus.length}
              className={`remote__payload__connect__info__button ${remoteConnected ? 'connected' : 'disconnected'}`}
              onClick={() => { setRemoteConnectionModal(prev => !prev) }}
            >
              {remoteAppLoader ? <CircularProgress size={14} color='inherit' /> : <RemoteAppSvg />}
            </button>
          </CustomToolTip>
        }

        <TLEContainer open={showTleInfo} close={toggle_scenario__info} />

        {/* Non-validate: show orbit info (positioned as before relative to command__center_header) */}
        {deploymentType !== SCENARIO_DEPLOYMENT_TYPES.VALIDATE && (
          <ScenariInfoContainer open={showScenarioInfo} close={toggle_scenario__info} />
        )}
        {(scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.SIMULATE || scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP) && <FaultInjectionHeader />}

      </div>
      {dashboard === "operate-1" && <div className='header_center_container'>
        <Notification
          show_all={showNotification}
          set_show_all={show_notification}
        />
      </div>}

      <div className='header_right_container'>
        <RightContainer />
      </div>
    </div >
  )
}

export default Header
